import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "styled-components";
import { useFormik } from "formik";
import Toggle from "react-toggle";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Lock, HelpCircle } from "lucide-react";

import {
  ModalFooterWrapper,
  ModalFooterLeft,
  ModalFooterRight,
} from "../../../sharedStyles";
import { Candidate, LocalityData } from "../../../interfaces";
import { GET_LOCALITIES } from "../../../GraphQL/settings/queries";
import {
  ADD_CANDIDATE,
  AMEND_CANDIDATE,
} from "../../../GraphQL/candidates/mutations";
import { GET_COMPANY_USERS } from "../../../GraphQL/settings/queries";
import { industries } from "../../../const";
import { useMutation, useQuery } from "@apollo/client";
import confirmWithModal from "../../Global/confirmWithModal";
import DraftEditor from "../../DraftEditor";
import ReactSelect from "react-select";
import AttachmentSection from "../../Attachments/AttachmentSection";
import {
  EToastPosition,
  EToastColorStatus,
} from "../../../interfaces";
import {
  GET_CANDIDATE_BY_ID,
  GET_PROJECT_CANDIDATE_BOARDS,
} from "../../../GraphQL/candidates/queries";
import { PartnerCheckboxes } from "../../Partners/PartnerMultiselect/Index";

interface Props {
  isNonATS: boolean;
  show: boolean;
  onHide: () => void;
  candidate: Candidate | null;
  reloadCandidates: () => any;
  atsServer: string;
  onCreateToast?: (
    color: EToastColorStatus,
    position: EToastPosition,
    message: string
  ) => void;
  onShowDelete: (data: boolean) => void;
  onSetDeleteId: (data: number) => void;
}

export const LabelNote = styled.span`
  color: #888;
  font-weight: normal;
  font-size: 16px;
`;

const SalaryInputs = styled.div`
  display: flex;
  justify-content: space-between;
  & input {
    width: 100%;
  }
  & select {
    width: 90px;
  }

  gap: 10px;
`;

const CheckWrap = styled.div`
  display: flex;
  flex-direction: row;
`;

const CandidateDetailFormSchema = Yup.object().shape({
  localityId: Yup.string().required("Required"),
  workType: Yup.string().required("Required"),
  summary: Yup.string()
    .test(
      "value",
      "Description is Required",
      (value) => value !== "<p><br></p>"
    )
    .required("Description is Required"),
  industry: Yup.string().required("Required"),
  yearsOfExperience: Yup.number()
    .min(0, "Minimum value should be 0")
    .required("Required and should be a number"),
  idealJobTitles: Yup.string().required("Required"),
  salaryMin: Yup.number()
    .min(40000, "Minimum salary is $40,000")
    .required("Required"),
  salaryMax: Yup.number()
    .required("Required")
    .moreThan(Yup.ref("salaryMin"), "Max salary must be more than min salary"),
  audience: Yup.string().required("Required"),
  partnershipIds: Yup.array()
    .of(Yup.number())
    .when("audience", {
      is: "PARTNER",
      then: (schema) => schema.min(1, "Select at least one partner"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const newCandidateDetailFormSchema = Yup.object().shape({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
});

const ToggleContainer = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: left;

  & div {
    margin: 0 4px;
  }
`;

const Hyphen = styled.div`
  padding-top: 5px;
`;




interface CandidateFormValues extends Omit<Partial<Candidate>, 'salaryMin' | 'salaryMax' | 'workType'> {
  salaryMin: number | string;
  salaryMax: number | string;
  localityId: string;
  share: boolean;
  partnershipIds: number[];
  industry: string;
  yearsOfExperience: string;
  idealJobTitles: string;
  audience: string;
  interviewed: boolean;
  workType: string;
  currency: string;
  ownerUserIds?: number[];
  projectCandidateBoardIds?: string[];
  isExplicitlyShared: boolean;
}

const CandidateDetailModal = ({
  isNonATS,
  show,
  onHide,
  candidate,
  reloadCandidates,
  atsServer,
  onCreateToast = () => { },
  onShowDelete,
  onSetDeleteId,
}: Props) => {


  const {
    error: errorCompany,
    loading: loadingCompany,
    data: dataCompany,
  } = useQuery(GET_COMPANY_USERS, {
    onCompleted: (data) => setCandidateOwners(data?.companyUsers?.items),
  });

  const {
    error: errorProjectBoards,
    loading: loadingProjectBoards,
    data: dataProjectBoards,
  } = useQuery(GET_PROJECT_CANDIDATE_BOARDS);




const shouldSkip = !show || !candidate || !candidate.id;

const { data: candidateByIdData, loading: candidateLoading } = useQuery(GET_CANDIDATE_BY_ID, {
  variables: {
    id: candidate?.id ? Number(candidate.id) : 0,
  },
  skip: shouldSkip,
  fetchPolicy: shouldSkip ? "cache-only" : "network-only",
});



  let initCandidateOwners = [];
  if (candidate?.candidateOwners) {
    for (let i in candidate?.candidateOwners)
      initCandidateOwners.push({
        value: candidate?.candidateOwners[i].userId,
        label: candidate?.candidateOwners[i].email,
      });
  }

  const [candidateOwnersValue, setCandidateOwnersValue] =
    useState(initCandidateOwners);
  const [candidateOwners, setCandidateOwners] = useState<any>([]);
  let ownerOptions = [];
  if (dataCompany)
    ownerOptions = candidateOwners.map((item: any) => ({
      value: item?.id,
      label: item?.email,
    }));

  let initProjectBoards = [];
  if (candidateByIdData) {
    for (let i in candidateByIdData?.candidates?.items[0]?.projectBoards) {
      initProjectBoards.push({
        value: candidateByIdData?.candidates?.items[0]?.projectBoards[i]?.id,
        label: candidateByIdData?.candidates?.items[0]?.projectBoards[i]?.title,
      });
    }
  }

  const [projectBoardsValue, setProjectBoardsValue] =
    useState(initProjectBoards);
  const [projectBoards, setProjectBoards] = useState<any>([]);

  const [addCandidate] = useMutation(ADD_CANDIDATE);
  const [amendCandidate] = useMutation(AMEND_CANDIDATE);
  const [sharing, setSharing] = useState(false);
  const { data } = useQuery<LocalityData>(GET_LOCALITIES);

  const localities = data?.localities?.items ? [...data.localities.items] : [];
  localities.sort((a, b) => {
    return a.state.localeCompare(b.state) || a.name.localeCompare(b.name);
  });

  useEffect(() => {
    let ownersValue = [];
    if (candidate?.candidateOwners) {
      for (let i in candidate?.candidateOwners)
        ownersValue.push({
          value: candidate?.candidateOwners[i].userId,
          label: candidate?.candidateOwners[i].email,
        });
    }
    setCandidateOwnersValue(ownersValue);

    let projectBoardValue = [];
    if (candidateByIdData) {
      for (let i in candidateByIdData?.candidates?.items[0]?.projectBoards) {
        projectBoardValue.push({
          value: candidateByIdData?.candidates?.items[0]?.projectBoards[i]?.id,
          label:
            candidateByIdData?.candidates?.items[0]?.projectBoards[i]?.title,
        });
      }
    }
    setProjectBoardsValue(projectBoardValue);

    setSharing(candidate?.status === "SHARED");
  }, [candidate, candidateByIdData]);

  useEffect(() => {
    let val = [];
    if (dataProjectBoards?.projectCandidateBoards?.items) {
      for (let i in dataProjectBoards?.projectCandidateBoards?.items) {
        val.push({
          value: dataProjectBoards?.projectCandidateBoards?.items[i]?.id,
          label: dataProjectBoards?.projectCandidateBoards?.items[i]?.title,
        });
      }
    }
    setProjectBoards(val);
  }, [dataProjectBoards]);

  const onDelete = () => {
    if (candidate?.id) onSetDeleteId(candidate?.id);
    onShowDelete(true);
    onHide();
  };



  //for pre-filling partnership ids
  useEffect(() => {
    const item = candidateByIdData?.candidates?.items?.[0]
    if (!item) return;
    const shared = candidateByIdData.candidates.items[0].sharedPartnerships || [];
    const ids = shared.map((p: any) => p.id);
    formik.setFieldValue('partnershipIds', ids);

  }, [candidateByIdData]);


  const formik = useFormik<CandidateFormValues>({
    enableReinitialize: true,
    initialValues: candidate
      ? {
        ...candidate,
        localityId: "" + candidate?.location?.localityId,
        share: false,
        summary: candidate?.summary ?? "",
        partnershipIds: candidate?.sharedPartnerships?.map(p => p.id) || [],
        currency: "AUD",
        workType: candidate.workType || "",
        salaryMin: candidate.salaryMin ?? "",
        salaryMax: candidate.salaryMax ?? "",
        industry: candidate.industry ?? "",
        yearsOfExperience: candidate.yearsOfExperience ?? "",
        idealJobTitles: candidate.idealJobTitles ?? "",
        audience: candidate.audience ?? "PRIVATE",
        interviewed: candidate.interviewed ?? false,
        isExplicitlyShared: candidate.isExplicitlyShared ?? false,
      }
      : {
        firstName: "",
        lastName: "",
        localityId: "",
        summary: "",
        industry: "",
        yearsOfExperience: "",
        idealJobTitles: "",
        salaryMin: "",
        salaryMax: "",
        share: false,
        audience: "PRIVATE",
        interviewed: false,
        workType: "",
        currency: "AUD",
        ownerUserIds: [],
        projectCandidateBoardIds: [],
        partnershipIds: [],
        isExplicitlyShared: false,
      },
    validateOnChange: true,
    validationSchema: candidate
      ? CandidateDetailFormSchema
      : CandidateDetailFormSchema.concat(newCandidateDetailFormSchema),
    onSubmit: async (values) => {
      if (values.share) {
        const confirmed = await confirmWithModal(
          <>
            <p>Before sharing a candidate, you confirm that</p>
            <ul>
              <li>
                You have received permission from the Candidate to be posted on
                the platform;
              </li>
              <li>
                there is a genuine opportunity for the Candidate to accept a
                role with a Client; and
              </li>
              <li>
                your agency has interviewed or screened the Candidate within the
                last three months.
              </li>
            </ul>
          </>
        );
        if (!confirmed) return;
      }

      if (candidate) {
        amendCandidate({
          variables: {
            ...values,
            localityId: parseInt(values.localityId),
            partnershipIds: (values.partnershipIds || [])
              .map(id => Number(id))
              .filter(id => id && id > 0 && !isNaN(id)),

          },
        })
          .then(() => {
            reloadCandidates();
            onCreateToast(
              EToastColorStatus.SUCCESS,
              EToastPosition.BOTTOM_END,
              "Candidate updated!"
            );
            onHide();
          })
          .catch(() => {
            onCreateToast(
              EToastColorStatus.FAILED,
              EToastPosition.BOTTOM_END,
              "Error while updating candidate, please ensure all fields are filled"
            );
          });
      } else {
        addCandidate({
          variables: {
            ...values,
            middleName: "",
            jobTitle: values.idealJobTitles,
            localityId: parseInt(values.localityId),
          },
        })
          .then((response) => {
            onCreateToast(
              EToastColorStatus.SUCCESS,
              EToastPosition.BOTTOM_END,
              "Candidate added successfully!"
            );
            reloadCandidates();
            onHide();
          })
          .catch(() => {
            onCreateToast(
              EToastColorStatus.FAILED,
              EToastPosition.BOTTOM_END,
              "Error while updating candidate, please ensure all fields are filled"
            );
          });
      }
    },
  });

  const fv = formik.values;







  const { errors, touched } = formik;

  const ErrorWrapper = ({ field }: { field: keyof typeof errors }) => {
    return (
      <>
        {errors && touched[field] ? (
          <div style={{ color: "red" }}>{errors[field]}</div>
        ) : null}
      </>
    );
  };

  return (
    <Dialog open={show} onOpenChange={onHide}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          {candidate?.firstName ? (
            <DialogTitle>
              {candidate?.firstName} {candidate?.lastName?.slice(0, 1)}.
              {candidate?.jobTitle ? ` - ${candidate.jobTitle}` : ""}
              {candidate?.location?.displayName
                ? ` - ${candidate.location.displayName}`
                : ""}
            </DialogTitle>
          ) : (
            <DialogTitle>New Candidate</DialogTitle>
          )}
        </DialogHeader>
        <div>
          <form onSubmit={formik.handleSubmit}>
            <br />
            <div className="grid grid-cols-1 gap-4">
              <div>
                <p>
                  <strong>Note:</strong> These details will be visible to
                  other agencies on the platform when sharing is activated.
                  Please ensure you check all fields (particularly the
                  Description/Profile) for any identifiable information.
                </p>
              </div>
            </div>
            {!candidate && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="space-y-2">
                      <Label htmlFor="formFamilyName">
                        Family name*{" "}
                        <LabelNote>
                          <Lock className="inline h-4 w-4" />
                        </LabelNote>
                      </Label>
                      <Input
                        id="formFamilyName"
                        type="text"
                        name="lastName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                      />
                      <ErrorWrapper field="lastName" />
                    </div>
                  </div>
                  <div>
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First name*
                        <LabelNote>
                          <Lock className="inline h-4 w-4" />
                        </LabelNote>
                      </Label>
                      <Input
                        id="firstName"
                        type="text"
                        name="firstName"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        required
                      />
                      <ErrorWrapper field="firstName" />
                    </div>
                  </div>
                </div>
                <br />
              </>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="space-y-2">
                    <Label htmlFor="formLocation">Location*</Label>
                    <select
                      id="formLocation"
                      name="localityId"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={fv.localityId}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    >
                      <option></option>
                      {localities.map((locality) => (
                        <option key={locality.id} value={locality.id}>
                          {locality.name} ({locality.state})
                        </option>
                      ))}
                    </select>
                    <ErrorWrapper field="localityId" />
                  </div>
                </div>
                <div>
                  <div className="space-y-2">
                    <Label htmlFor="formType">Type</Label>
                    <select
                      id="formType"
                      name="workType"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={fv.workType}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value=""></option>
                      <option value="PERMANENT">PERMANENT</option>
                      <option value="TEMP">CONTRACT/TEMP</option>
                      <option value="ANY">ANY</option>
                    </select>
                    <ErrorWrapper field="workType" />
                  </div>
                </div>
              </div>
              <div>
                <div className="space-y-2">
                  <Label htmlFor="formRoleSalary">Salary range (min. $40,000)*</Label>
                  <SalaryInputs>
                    <Input
                      type="number"
                      name="salaryMin"
                      placeholder="Min."
                      required
                      value={fv.salaryMin}
                      onChange={formik.handleChange}
                      onBlur={(...args) => {
                        formik.handleBlur(...args);
                        formik.validateField("salaryMax");
                      }}
                    />
                    <div>-</div>
                    <Input
                      type="number"
                      name="salaryMax"
                      placeholder="Max."
                      value={fv.salaryMax}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      required
                    />
                    <select
                      name="currency"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={fv.currency}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value="AUD">AUD</option>
                      <option value="NZD">NZD</option>
                    </select>
                  </SalaryInputs>
                  <ErrorWrapper field="salaryMin" />
                  <ErrorWrapper field="salaryMax" />
                </div>
              </div>
            </div>
            <br />
            <div className="grid grid-cols-1 gap-4">
              <div>
                <div className="space-y-2">
                  <Label htmlFor="formRole">Description/Profile*</Label>
                  <DraftEditor formik={formik} />
                </div>
                <ErrorWrapper field="summary" />
              </div>
            </div>
            <br />

            {candidate && (
              <>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <AttachmentSection
                      entityId={candidate.id}
                      type="candidate"
                      onUploadComplete={() => {
                        // Trigger refresh of attachment list
                      }}
                      onAttachmentDeleted={() => {
                        // Handle attachment deletion if needed
                      }}
                    />
                  </div>
                </div>
                <br />
              </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="space-y-2">
                  <Label htmlFor="formIndustry">Industry*</Label>
                  <select
                    id="formIndustry"
                    name="industry"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={fv.industry}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  >
                    <option></option>
                    {industries.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                  <ErrorWrapper field="industry" />
                </div>
              </div>
              <div>
                <div className="space-y-2">
                  <Label htmlFor="formRoleExperience">Years experience*</Label>
                  <Input
                    id="formRoleExperience"
                    type="number"
                    name="yearsOfExperience"
                    required
                    value={fv.yearsOfExperience}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <ErrorWrapper field="yearsOfExperience" />
                </div>
              </div>
            </div>
            <br />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="space-y-2">
                  <Label htmlFor="formRoleIdealJobTitle">Ideal job titles*</Label>
                  <Input
                    id="formRoleIdealJobTitle"
                    type="text"
                    name="idealJobTitles"
                    required
                    value={fv.idealJobTitles}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <ErrorWrapper field="idealJobTitles" />
                </div>
              </div>
              {!loadingCompany && !errorCompany && dataCompany && (
                <div>
                  <div className="space-y-2">
                    <Label htmlFor="candidateOwners">Owners</Label>
                    <ReactSelect
                      onChange={(data: any) => {
                        let ownerIds = [];
                        for (let i in data)
                          ownerIds.push(Number(data[i]?.value));
                        formik.setFieldValue("ownerUserIds", ownerIds);
                        setCandidateOwnersValue(data);
                      }}
                      name="candidateOwners"
                      options={ownerOptions}
                      isMulti={true}
                      value={candidateOwnersValue}
                    />
                  </div>
                </div>
              )}
            </div>
            <br />
            <div className="grid grid-cols-1 gap-4">
              <div>
                <CheckWrap>
                  <input
                    type="checkbox"
                    id="interviewed"
                    name="interviewed"
                    checked={fv.interviewed}
                    onChange={() =>
                      formik.setFieldValue("interviewed", !fv.interviewed)
                    }
                    className="mr-2"
                  />
                  <label htmlFor="interviewed">
                    I have received permission from the candidate to be represented on Role Relay in the last 3 months
                  </label>
                  {" "}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="inline h-4 w-4 cursor-help ml-2" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Role Relay's terms dictate that:</p>
                        <ul>
                          <li>
                            Only candidates that you have engaged with in
                            the last 3 months can be represented on the
                            platform.
                          </li>
                        </ul>
                        <ul>
                          <li>
                            You must have written permission from the
                            candidate to represent them on the platform.
                          </li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CheckWrap>
              </div>
            </div>
            <hr />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="space-y-2">
                  <Label>
                    Sharing{" "}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="inline h-4 w-4 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            <strong>Shared for Collaboration</strong> will push
                            this candidate to the Role Relay Network based on
                            your <strong>Audience</strong> selection. Candidates
                            you do not wish to share with the network can be set
                            to <strong>Draft</strong>.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <ToggleContainer>
                    <span>Draft</span>
                    <Toggle
                      name="shared"
                      icons={false}
                      onChange={(evt) => {
                        setSharing(evt.target.checked);
                      }}
                      checked={sharing}
                    />{" "}
                    <span>Shared for collaboration</span>
                  </ToggleContainer>
                </div>
                <div className="space-y-2">
      <Label>
        Recruiter Visibility {" "}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="inline h-4 w-4 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p>
                <strong>ON:</strong> Other agencies will see your name and 
                agency details in the community board.
                <br />
                <strong>OFF:</strong> You will remain "Confidential" unless 
                the community board is set to public.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Label>
      <ToggleContainer>
        <span>Confidential</span>
        <Toggle
          name="isExplicitlyShared"
          icons={false}
          checked={fv.isExplicitlyShared}
          onChange={(evt) => {
            formik.setFieldValue("isExplicitlyShared", evt.target.checked);
          }}
        />{" "}
        <span>Show My Details</span>
      </ToggleContainer>
    </div>
              </div>
              
              
              
              <div>
                <div className="space-y-2">
                  <Label htmlFor="formAudience">
                    Audience{" "}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="inline h-4 w-4 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            Select which audience you would like to share
                            the Candidate with.
                          </p>
                          <ul>
                            <li>
                              <strong>Private</strong> - only flagged for
                              Role Relay automatic matching. You will
                              receive notifications of opportunities Role
                              Relay detects for this candidate, but their
                              anonymous details will not be searchable for
                              other agencies on the Public or Partner
                              Candidate Boards.
                            </li>
                            <li>
                              <strong>Partners Only</strong> - flagged for
                              Role Relay automatic matching, and listed on
                              the Partner Candidate Board. The candidate's
                              anonymous details will not be searchable on
                              the Public Candidate Board.
                            </li>
                            <li>
                              <strong>Public</strong> - flagged for
                              matching, and listed on the Partner and Public
                              Candidate Board.
                            </li>
                            <li>
                              <strong>Community</strong> - flagged for
                              matching, and listed on the Community
                              Candidate Board visibile to other community
                              members.
                            </li>
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <select
                    id="formAudience"
                    name="audience"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={fv.audience}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value=""></option>
                    <option value="PRIVATE">Private</option>
                    <option value="PARTNER">Partners only</option>
                    <option value="COMMUNITY">Community only</option>
                    <option value="PUBLIC">
                      Public (incl. Partners & Communities)
                    </option>
                  </select>
                  <ErrorWrapper field="audience" />

                  <PartnerCheckboxes
                    values={formik.values}
                    setFieldValue={formik.setFieldValue}
                    setFieldTouched={formik.setFieldTouched}
                    touched={formik.touched}
                    errors={formik.errors}

                  />
                </div>
              </div>
            </div>
            <br />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div></div>
              <div>
                {!loadingProjectBoards &&
                  !errorProjectBoards &&
                  dataProjectBoards && (
                    <div className="space-y-2">
                      <Label htmlFor="projectBoards">Projects</Label>
                      <ReactSelect
                        onChange={(data: any) => {
                          let candidateBoardIds = [];
                          for (let i in data)
                            candidateBoardIds.push(data[i]?.value);
                          formik.setFieldValue(
                            "projectCandidateBoardIds",
                            candidateBoardIds
                          );
                          setProjectBoardsValue(data);
                        }}
                        name="projectBoards"
                        isMulti={true}
                        options={projectBoards}
                        value={projectBoardsValue}
                      />
                    </div>
                  )}
              </div>
            </div>
          </form>

          <hr />
          <p>
            <strong>What happens next?</strong>
          </p>
          <p>
            Your Candidate will be shared with the Role Relay network based
            on the Audience you have selected. Only the anonymous details
            you have provided will be shared with other agencies.
          </p>
        </div>

        <DialogFooter>
          <ModalFooterWrapper>
            <ModalFooterLeft>
              {candidate && (
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Button
                      variant="link"
                      onClick={(e) => {
                        e.preventDefault();
                        onDelete();
                      }}
                    >
                      Remove this candidate from Role Relay
                    </Button>
                  </div>
                </div>
              )}
            </ModalFooterLeft>
            <ModalFooterRight>
              <Button className="mx-1" variant="secondary" onClick={onHide}>
                Cancel
              </Button>
              <Button
                className="mx-1"
                variant="default"
                disabled={formik.isSubmitting}
                onClick={() => {
                  formik.setFieldValue("share", sharing);
                  formik.submitForm();
                }}
              >
                Save
              </Button>
            </ModalFooterRight>
          </ModalFooterWrapper>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateDetailModal;